import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import FiscalYearSelector from './FiscalYearSelector';
import OnboardingTour, { useOnboarding } from './OnboardingTour';
import ImpersonationBanner from './ImpersonationBanner';
import ErrorBoundary from './ErrorBoundary';
import {
  LayoutDashboard,
  Settings,
  FileInput,
  Package,
  HelpCircle,
  Moon,
  Sun,
  LogOut,
  Globe,
  Leaf,
  Calendar,
  Shield,
  GraduationCap,
  Layers,
  Users,
  Menu
} from 'lucide-react';

const Layout = () => {
  const { user, logout, isCompanyAdmin, isCsAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { restartTour } = useOnboarding();

  // RESP-1 — drawer de navigation off-canvas, SOUS lg uniquement. L'état est
  // sans effet >= lg : toutes les classes qu'il pilote sont en variante
  // max-lg:, donc absentes du CSS >= lg (invariant byte-identique).
  const [navOpen, setNavOpen] = useState(false);
  // Fermeture à la navigation. Piloté par le pathname plutôt que par un
  // onClick sur chaque NavLink : couvre d'un coup les liens ET les
  // navigate() programmatiques (carte profil, bouton « créer un exercice »).
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Navigation principale (fonctions métier)
  const mainNavItems = [
    { path: '/general-info', icon: Settings, label: t('nav.generalInfo') },
    { path: '/fiscal-years', icon: Calendar, label: language === 'fr' ? 'Exercices' : 'Geschäftsjahre' },
    { path: '/data-entry', icon: FileInput, label: t('nav.dataEntry') },
    { path: '/products', icon: Package, label: t('nav.products') },
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    // Team link - only shown if user is company admin
    ...(isCompanyAdmin ? [{ path: '/team', icon: Users, label: t('team.title') }] : []),
    // Platform super-admin links - only shown if user is cs_admin
    ...(isCsAdmin ? [{ path: '/admin', icon: Shield, label: t('nav.admin') || 'Administration' }] : []),
    ...(isCsAdmin ? [{ path: '/curation', icon: Layers, label: language === 'fr' ? 'Curation' : 'Kuratierung' }] : []),
  ];

  // Support/Aide (séparé visuellement)
  const supportNavItems = [
    { path: '/assistance', icon: HelpCircle, label: language === 'fr' ? 'Assistance' : 'Hilfe' },
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-slate-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
      {/* RESP-1 — overlay de fermeture, rendu AVANT l'aside dans le DOM :
          à z-index ÉGAL (z-40), l'ordre DOM fait gagner l'aside, qui reste
          donc au-dessus. Aucune strate z-index nouvelle n'est créée, et
          l'ensemble reste sous les drawers applicatifs (50/60), sous le
          viewport Toast (70) et sous le tour d'onboarding (10000). */}
      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
          data-testid="nav-overlay"
          className="hidden max-lg:block max-lg:fixed max-lg:inset-0 max-lg:bg-black/50 max-lg:z-40"
        />
      )}

      {/* Sidebar — >= lg : flex item sticky, strictement inchangée.
          < lg : drawer off-canvas. `max-lg:fixed` prend le pas sur `sticky`
          (même propriété CSS), et les `top-0` / `h-screen` déjà en base
          donnent exactement la géométrie voulue une fois en fixed : rien à
          neutraliser, la bascule sticky -> fixed est portée par une seule
          classe. */}
      <aside className={`w-64 sticky top-0 self-start h-screen flex flex-col z-40 max-lg:fixed max-lg:left-0 max-lg:transition-transform ${navOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'} ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} border-r`}>
        {/* Logo */}
        <div className="p-6 border-b border-inherit">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">CarbonScope</h1>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('nav.tagline')}</p>
            </div>
          </div>
        </div>

        {/* Fiscal Year Selector */}
        <div className="px-4 py-3 border-b border-inherit">
          <FiscalYearSelector onCreateNew={() => navigate('/fiscal-years')} />
        </div>

        {/* Navigation principale */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {mainNavItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${path === '/' ? 'dashboard' : path.slice(1)}`}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                ${isActive 
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' 
                  : isDark 
                    ? 'text-slate-300 hover:bg-slate-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
          
          {/* Séparateur + Section Support */}
          <div className={`my-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`} />
          
          {supportNavItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              data-testid={`nav-${path.slice(1)}`}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                ${isActive 
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30' 
                  : isDark 
                    ? 'text-slate-300 hover:bg-slate-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Section Préférences (Langue + Thème côte à côte) */}
        <div className={`px-4 py-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="flex gap-2">
            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all ${
                isDark 
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'fr' ? 'FR' : 'DE'}</span>
            </button>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all ${
                isDark 
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="text-sm font-medium">{isDark ? (language === 'fr' ? 'Clair' : 'Hell') : (language === 'fr' ? 'Sombre' : 'Dunkel')}</span>
            </button>
          </div>
        </div>

        {/* Section Profil utilisateur */}
        <div className={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          {/* Relancer le tutoriel */}
          <button
            onClick={restartTour}
            data-testid="restart-tour-btn"
            className={`w-full flex items-center gap-2 px-3 py-2 mb-3 rounded-lg text-sm transition-all ${
              isDark 
                ? 'text-slate-400 hover:text-indigo-400 hover:bg-slate-700/50' 
                : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            <GraduationCap className="w-4 h-4" />
            <span>{language === 'fr' ? 'Relancer le tutoriel' : 'Tutorial starten'}</span>
          </button>
          <div
            onClick={() => navigate('/account')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${
              isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium truncate">{user?.name || 'User'}</p>
                  <Settings className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                </div>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user?.email}</p>
              </div>
              <button
                onClick={e => { e.stopPropagation(); logout(); }}
                data-testid="logout-btn"
                title={language === 'fr' ? 'Déconnexion' : 'Abmelden'}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'hover:bg-slate-600' : 'hover:bg-gray-300'}`}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content.
          POLISH-1 — sous lg, le min-width par défaut de ce flex item vaut
          « auto » : la largeur intrinsèque du contenu le plus exigeant se
          propage jusqu'ici et élargit la page entière au-delà du viewport
          (mesuré : +13px sur la saisie, et une part des débordements du
          tableau de bord et des informations générales). On libère ce
          minimum sous lg seulement — au-dessus, rien ne dépasse et le rendu
          reste strictement identique. */}
      <main className={`flex-1 min-h-screen max-lg:min-w-0 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}>
        {/* RESP-1 volet 3 — p-8 conservé en BASE (donc >= lg identique) ;
            seule une variante max-lg: le resserre sous lg, où 32px de marge
            de chaque côté sur 390px de large coûtent 16% de la largeur. */}
        <div className="p-8 max-lg:p-4">
          {/* RESP-1 — hamburger, visible SOUS lg seulement. `hidden` est une
              classe de BASE déjà présente au bundle : aucune règle >= lg
              n'est ajoutée, et display:none n'occupe aucun espace. Tout le
              reste du style est en max-lg:. */}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label={language === 'fr' ? 'Ouvrir le menu' : 'Menü öffnen'}
            aria-expanded={navOpen}
            data-testid="nav-hamburger"
            className={`hidden max-lg:flex max-lg:items-center max-lg:gap-2 max-lg:mb-4 max-lg:px-4 max-lg:py-3 max-lg:rounded-xl max-lg:font-medium max-lg:border ${isDark ? 'max-lg:bg-slate-800 max-lg:border-slate-700 max-lg:text-slate-200' : 'max-lg:bg-white max-lg:border-gray-200 max-lg:text-gray-700'}`}
          >
            <Menu className="w-5 h-5" />
            <span>{language === 'fr' ? 'Menu' : 'Menü'}</span>
          </button>

          {/* Boundary de page : un crash de page préserve la coquille nav (fallback local). */}
          <ErrorBoundary fallback={
            <div role="alert" className={`rounded-xl border p-6 text-center ${isDark ? 'border-slate-700 bg-slate-800 text-slate-200' : 'border-gray-200 bg-white text-gray-800'}`}>
              <p className="font-semibold">Cette page a rencontré une erreur.</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Naviguez ailleurs ou rechargez la page.</p>
              <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">Recharger</button>
            </div>
          }>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
      </div>

      {/* FEAT-01: Onboarding Tour */}
      <OnboardingTour />
    </div>
  );
};

export default Layout;
